export const TENANT_STATUS = ['onboarding', 'active', 'paused', 'cancelled'] as const;
export const ADMIN_ROLE = ['accountant', 'superadmin'] as const;
export const TAX_REGIMEN = ['iva_irp', 'ire_simple'] as const;
export const PLAN = ['esencial', 'pro'] as const;
export const BILLING_CYCLE = ['monthly', 'annual'] as const;
export const SUBSCRIPTION_STATUS = ['trialing', 'active', 'past_due', 'cancelled'] as const;

export const INVOICE_DIRECTION = ['purchase', 'sale'] as const;
export const INVOICE_TIPO_DOCUMENTO = [
  'factura',
  'nota_credito',
  'nota_debito',
  'autofactura',
  'boleta_venta',
] as const;
export const INVOICE_CONDITION = ['contado', 'credito'] as const;
// Origen del comprobante de gasto: 'manual' = foto/OCR o carga manual;
// 'set' = comprobante electrónico sincronizado de Marangatu (auto-aprobado).
export const INVOICE_SOURCE = ['manual', 'set'] as const;
export const INVOICE_STATUS = [
  'pending_extraction',
  'pending_review',
  'approved',
  'rejected',
  'included_in_declaration',
] as const;
export const NATURALEZA_OPERACION = ['bienes', 'servicios', 'mixto'] as const;
// Categorías de CONTROL DE GASTOS personal (no fiscales). El motor F.120/IRP
// NO usa este campo en cálculos — es informativo para la sección Finanzas.
// Las 8 primeras existen desde el principio; las 30 siguientes se agregaron
// en mig 036 para granularidad fina. Orden: primero el grupo "comer/beber",
// después "casa", "salud/educación", "movilidad", "personal", "ocio/digital",
// "finanzas/profesional", y al final "otro" como fallback.
export const CATEGORIA_GASTO = [
  // Originales (1-8) — NO REORDENAR para no romper queries existentes.
  'alimentacion',
  'transporte',
  'salud',
  'educacion',
  'vivienda',
  'servicios',
  'insumos',
  'otro',
  // Comer/beber (más específicas que 'alimentacion').
  'supermercado',
  'restaurante',
  'delivery',
  'bebidas',
  // Casa.
  'vivienda_alquiler',
  'expensas',
  'servicios_publicos',     // luz, agua
  'internet_telefono',
  'gas',
  'mantenimiento_hogar',    // limpieza, plomería, pintura
  'electrodomesticos',
  'muebles_decoracion',
  // Salud/educación específicas.
  'farmacia',
  'libros_cursos',
  // Movilidad.
  'combustible',
  'vehiculo',               // mantenimiento, repuestos, lavado
  // Personal.
  'vestimenta',
  'peluqueria_belleza',
  // Ocio/digital.
  'entretenimiento',        // cine, teatro, conciertos
  'streaming',              // Netflix, Spotify, etc.
  'mascotas',
  'deportes_gym',
  'viajes',
  'tecnologia',             // gadgets, accesorios
  'software_suscripciones',
  // Finanzas/profesional.
  'seguros',
  'impuestos_tasas',
  'comisiones_bancarias',
  'honorarios_profesionales',
  // Misc.
  'regalos_donaciones',
] as const;
export const EXTRACTION_PROVIDER = ['gemini', 'claude', 'manual'] as const;
export const EXTRACTION_JOB_STATUS = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'requires_human',
] as const;

/**
 * Prioridad de extracción — define el path por el que se procesa la factura.
 *
 *  - `batch` (default): la factura va a la cola de Gemini Batch API. Latencia
 *    P95 ~5-30 min, costo ~50% menor. Es lo que usamos para uploads no
 *    interactivos (subida masiva, pasivos cargados al final del día, etc).
 *
 *  - `sync`: la factura se procesa al toque via OpenRouter sync (Gemini 2.5
 *    Flash o fallback GPT-4o). Latencia <30s, costo full. Lo reservamos para
 *    flows interactivos donde el usuario espera respuesta inmediata:
 *      * Onboarding — primera factura del usuario (efecto wow).
 *      * Consulta puntual ("¿esta factura es deducible?") — necesita extracción
 *        para responder en la misma vuelta de conversación.
 *      * Reintento manual desde el panel admin del contador.
 *
 * Regla: **batch por default, sync por excepción explícita**. El sync se
 * marca desde el endpoint que crea la invoice (ej: webhook del bot detecta
 * que es la primera factura → priority=sync).
 */
export const EXTRACTION_PRIORITY = ['sync', 'batch'] as const;

/**
 * Estados del ciclo de vida de un batch de Gemini.
 *
 *  - `pending`: armado localmente, todavía no submited a la API.
 *  - `submitted`: enviado a Gemini, esperando que arranque.
 *  - `processing`: Gemini lo está corriendo (metadata.state = RUNNING).
 *  - `completed`: terminó OK, output descargado y aplicado a invoices.
 *  - `failed`: Gemini reportó error de batch entero (no líneas individuales).
 *  - `cancelled`: cancelamos nosotros (ej: el operador desde el admin panel).
 */
export const INVOICE_BATCH_STATUS = [
  'pending',
  'submitted',
  'processing',
  'completed',
  'failed',
  'cancelled',
] as const;

export const DECLARATION_KIND = ['iva', 'irp', 'ire_simple'] as const;
export const DECLARATION_FORM_CODE = ['F.120', 'F.501', 'F.515'] as const;
export const DECLARATION_STATUS = [
  'draft',
  'pending_review',
  'approved',
  'submitted',
  'paid',
  'rectified',
] as const;

export const MESSAGE_DIRECTION = ['inbound', 'outbound'] as const;
export const MESSAGE_KIND = ['text', 'image', 'document', 'audio', 'video', 'system'] as const;
export const MESSAGE_INTENT = ['invoice_upload', 'query', 'command', 'unknown'] as const;

export const FILE_KIND = [
  'invoice_image',
  'payment_receipt',
  'set_submission',
  'other',
] as const;
export const FILE_STORAGE_PROVIDER = ['do_spaces', 'local'] as const;
export const FILE_UPLOADED_VIA = ['whatsapp', 'admin_panel', 'api'] as const;

export const NOTIFICATION_KIND = [
  'due_date_3d',
  'due_date_today',
  'declaration_ready',
  'invoice_processed',
  'payment_failed',
  'monthly_summary',
] as const;
export const NOTIFICATION_CHANNEL = ['whatsapp', 'email'] as const;

export const VALID_TAX_RATES = [0, 5, 10] as const;

// --- Mensajería ---
export const MESSAGING_PLATFORM = [
  'whatsapp_baileys',     // WhatsApp Web no oficial (Baileys)
  'whatsapp_official',    // WhatsApp Business API oficial (Meta)
  'telegram',
] as const;

export const MESSAGING_BOT_STATUS = [
  'disconnected',
  'pairing',
  'connected',
  'error',
] as const;
